import { ALL_COUNTRIES, COUNTRY_NAMES } from "@/utils/countries";
import { ENERGY_TECHNOLOGIES, TECHNOLOGIES } from "@/types/technology";
export { ALL_COUNTRIES, COUNTRY_NAMES };

// Countries with their own registry signup flow. A producer anywhere else gets the international
// flow: it creates the company, notifies us, and ends on booking a demo.
export const OWN_SIGNUP_FLOW_COUNTRIES = ["EE", "LV", "LT", "SE", "FI"];

// EU member states (27 countries as of 2024)
export const EU_COUNTRIES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
];

export const SUPPORTED_COUNTRIES_WITH_LABELS = [
  { value: "AT", label: "Austria" },
  { value: "BE", label: "Belgium" },
  { value: "BG", label: "Bulgaria" },
  { value: "HR", label: "Croatia" },
  { value: "CY", label: "Cyprus" },
  { value: "CZ", label: "Czechia" },
  { value: "DK", label: "Denmark" },
  { value: "EE", label: "Estonia" },
  { value: "ES", label: "Spain" },
  { value: "FI", label: "Finland" },
  { value: "FR", label: "France" },
  { value: "DE", label: "Germany" },
  { value: "GR", label: "Greece" },
  { value: "HU", label: "Hungary" },
  { value: "IS", label: "Iceland" },
  { value: "IE", label: "Ireland" },
  { value: "IT", label: "Italy" },
  { value: "LV", label: "Latvia" },
  { value: "LI", label: "Liechtenstein" },
  { value: "LT", label: "Lithuania" },
  { value: "LU", label: "Luxembourg" },
  { value: "MT", label: "Malta" },
  { value: "NL", label: "Netherlands" },
  { value: "NO", label: "Norway" },
  { value: "PL", label: "Poland" },
  { value: "PT", label: "Portugal" },
  { value: "RO", label: "Romania" },
  { value: "SK", label: "Slovakia" },
  { value: "SI", label: "Slovenia" },
  { value: "SE", label: "Sweden" },
  { value: "CH", label: "Switzerland" },
  { value: "GB", label: "United Kingdom" },
];

// "Any AIB" cancellation-flow marker (DEV-576). A sentinel the cancel editor's registry dropdown
// holds while editing; it is never a real registry (kept out of SUPPORTED_REGISTRIES_WITH_LABELS) and
// materializes to "NO" on submit, carrying is_any_aib=true. Distinct from the ANY_AIB_REGISTRY
// CancellationMode enum value (a different concept).
export const ANY_AIB_REGISTRY_SENTINEL = "ANY_AIB";

export const SUPPORTED_REGISTRIES_WITH_LABELS = [
  { value: "AT", operator: "E-Control" },
  { value: "BE-BRU", operator: "BRUGEL" },
  { value: "BE-FED", operator: "CREG" },
  { value: "BE-FLA", operator: "VREG" },
  { value: "BE-WAL", operator: "e-CWaPE" },
  { value: "HR", operator: "HROTE" },
  { value: "CY", operator: "TSO-Cy" },
  { value: "CZ", operator: "OTE" },
  { value: "DK", operator: "Energinet" },
  { value: "EE", operator: "Elering" },
  { value: "FI", operator: "Finextra" },
  { value: "FR", operator: "EEX" },
  { value: "DE", operator: "HKNR" },
  { value: "GR", operator: "DAPEEP" },
  { value: "HU", operator: "MEKH" },
  { value: "IS", operator: "Landsnet" },
  { value: "IE", operator: "SEMO" },
  { value: "IT", operator: "GSE" },
  { value: "LV", operator: "AST" },
  { value: "LT", operator: "Litgrid" },
  { value: "LU", operator: "ILR" },
  { value: "NL", operator: "VertiCer" },
  { value: "NO", operator: "Statnett" },
  { value: "PT", operator: "EEGO" },
  { value: "RS", operator: "EMS" },
  { value: "SK", operator: "OKTE" },
  { value: "SI", operator: "Borzen" },
  { value: "PL", operator: "TGE" },
  { value: "ES", operator: "CNMC" },
  { value: "SE", operator: "Energy Agency" },
  { value: "CH", operator: "Pronovo" },
  { value: "GB", operator: "Ofgem" },
];

export const LANGUAGE_TO_COUNTRY = {
  et: "EE",
  lv: "LV",
  lt: "LT",
  sv: "SE",
  fi: "FI",
  es: "ES",
} as const;

export const ADMIN_IDS = {
  // TODO add proper access control
  FULL_ACCESS: [16, 24, 513, 548], // CEO and CFO admin IDs with full access to commission data
};

export const PORTFOLIO_TYPES = [
  { portfolio_type: "UNALLOCATED", name: "Unallocated" },
  { portfolio_type: "FORWARD", name: "Forward" },
  { portfolio_type: "HOLD", name: "Hold" },
  { portfolio_type: "SPOT_MARKET_PRICE", name: "Spot Market Price" },
  { portfolio_type: "SPOT_STX", name: "Spot STX" },
  { portfolio_type: "SPOT_MIN_PRICE", name: "Spot Min Price" },
  { portfolio_type: "OWN_USE", name: "Own use" },
];

export const SELLABLE_PORTFOLIO_TYPES = [
  "SPOT_MARKET_PRICE",
  "SPOT_STX",
  "SPOT_MIN_PRICE",
];

export const INFORMATIONAL_PORTFOLIO_TYPES = new Set([
  "HOLD",
  "PPA",
  "VIRTUAL",
  "UNALLOCATED",
  "OWN_USE",
]);

// List of technology types with their prefix codes (projected technologies, registry order)
export const TECHNOLOGY_TYPES = ENERGY_TECHNOLOGIES.map((key) => ({
  tech_code_prefix: TECHNOLOGIES[key].prefix,
  name: TECHNOLOGIES[key].name,
}));

// Countries in the standard AIB Guarantees of Origin certification scheme.
// Keep in sync with `_AIB_MEMBER_CODE_TO_ISO2_COUNTRY` in app/utils/eecs_utils.py.
// Mirrored by AIB_GC_COUNTRIES in app/constants.py. tests/forwards2/test_aib_country_parity.py parses
// the two array literals below out of this file, and holds the backend's standard list to the literal
// that client/tests/constants.spec.ts pins the filtered one to, so drift on either side fails a test.
export const AIB_GC_COUNTRIES = [
  "AT",
  "BE",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LT",
  "LU",
  "LV",
  "NL",
  "NO",
  "PT",
  "RS",
  "SE",
  "SI",
  "SK",
];

// AIB GC member countries whose Guarantees of Origin trade at a premium.
// They are excluded from the "Standard AIB GC" quick filter (see below), but
// are still valid AIB GC members and remain in AIB_GC_COUNTRIES above.
export const PREMIUM_AIB_GC_COUNTRIES = ["AT", "CH", "DE", "NL"];

// AIB GC members that sell as their own product rather than as standard AIB
// GC. Belgium issues as BE-FLA / BE-BRU, which the Transactions view's region
// filter never matched, so leaving it out keeps the forwards picker's filter
// the same product as the spot sale's.
export const NON_STANDARD_AIB_GC_COUNTRIES = ["BE"];

// AIB GC countries at standard (non-premium) pricing, used by the
// "Standard AIB GC" quick filters on the Transactions view and the forwards
// picker. Excludes the premium and non-standard countries above, and IS
// (Iceland) which is an AIB member but is not part of the GC scheme.
export const STANDARD_AIB_GC_COUNTRIES = AIB_GC_COUNTRIES.filter(
  (country) =>
    !PREMIUM_AIB_GC_COUNTRIES.includes(country) &&
    !NON_STANDARD_AIB_GC_COUNTRIES.includes(country) &&
    country !== "IS"
);

// Make sure to update these URLs in the Backend constants.py as well !!!!!!
export const FORWARD_OUTLOOK_URL =
  "https://storage.googleapis.com/soldera-marketing-public/Outlooks/Soldera_2026_November_Outlook.pdf";

export const SHORT_FORWARD_OUTLOOK_URL =
  "https://storage.googleapis.com/soldera-marketing-public/Outlooks/Soldera_2026_November_Outlook_Short.pdf";

export const FORWARD_OUTLOOK_LAST_UPDATED = "November 2026";

// Market Insights Data URLs
export const EU_GENERATION_DATA_URL =
  "https://static.soldera.org/gendata/eu_gen_processed.json";

export const RESERVOIR_LEVELS_URLS = {
  NORWAY_TOTAL:
    "https://static.soldera.org/reservoir/static/future_predictions_NO.csv",
  NO1: "https://static.soldera.org/reservoir/static/future_predictions_NO1.csv",
  NO2: "https://static.soldera.org/reservoir/static/future_predictions_NO2.csv",
  NO3: "https://static.soldera.org/reservoir/static/future_predictions_NO3.csv",
  NO4: "https://static.soldera.org/reservoir/static/future_predictions_NO4.csv",
  NO5: "https://static.soldera.org/reservoir/static/future_predictions_NO5.csv",
};

// Every weekly gridworks run is kept in this bucket; the reservoir card lists and reads it directly
// (public read + list, CORS open) to show earlier forecasts.
export const RESERVOIR_PREDICTIONS_LIST_URL =
  "https://storage.googleapis.com/storage/v1/b/soldera-production-predictions/o";
export const RESERVOIR_PREDICTIONS_FILES_URL =
  "https://storage.googleapis.com/soldera-production-predictions";

/** Mirrors SOLDERA_SUPPORT_EMAIL in app/constants.py. */
export const SOLDERA_SUPPORT_EMAIL = "support@soldera.org";

// Company Views
// Mirrors CompanyView in app/models/account_models.py (Company.dashboard_view). Exactly one per
// company: it picks the dashboard and the segment, and with it which feature flags apply.
export const COMPANY_VIEWS = {
  default: "Default (small producer, big producer above 1 MW)",
  big_producer: "Big producer",
  trader: "Trader",
  corporate: "Corporate",
} as const;

export type CompanyView = keyof typeof COMPANY_VIEWS;

const ALL_COMPANY_VIEWS = Object.keys(COMPANY_VIEWS) as CompanyView[];
/**
 * Whether a view's companies own production devices; traders and corporations own none. A
 * Record, so a new view fails typecheck until it is decided. Mirrors IS_PRODUCER_VIEW in
 * app/models/account_models.py.
 */
export const IS_PRODUCER_VIEW: Record<CompanyView, boolean> = {
  default: true,
  big_producer: true,
  trader: false,
  corporate: false,
};
const PRODUCER_VIEWS = ALL_COMPANY_VIEWS.filter(
  (view) => IS_PRODUCER_VIEW[view]
);

/**
 * Where a producer who self-signs up outside Estonia goes once their company exists: a sales rep
 * walks them through onboarding on the call. `organisation=producer` is meant to preselect the demo
 * form's Producer option (soldera-org/landing-page, public/js/demo-booking.js).
 */
export const PRODUCER_DEMO_BOOKING_URL =
  "https://www.soldera.org/demo?organisation=producer";

// Company Feature Flags
// Feature flags are defined in the CompanyFeatureFlags model in app/models/account_models.py
// When adding a new feature flag:
// 1. Add the field to CompanyFeatureFlags model in app/models/account_models.py
// 2. Add it with its human-readable label and admin tooltip description to the FEATURE_FLAG_GROUPS group whose views it applies to

/**
 * Sections of the admin company screen, and the single list of company flag keys and labels. The
 * admin screen shows a group only for the company views it lists — a flag outside them has no
 * effect there (e.g. only the trader dashboard reads buy_EACs_enabled).
 */
export const FEATURE_FLAG_GROUPS = [
  {
    id: "trust",
    label: "Trust & onboarding",
    views: ALL_COMPANY_VIEWS,
    flags: [
      {
        key: "trusted_customer",
        label: "Trusted Customer",
        description:
          "For a customer Soldera has vetted, typically a corporation at onboarding. Lifts the abuse limits put on self-signup accounts (team invites, cancellation-statement uploads, the AI query cap) for every account linked to the company.",
      },
      {
        key: "allocation_ready",
        label: "Ready for Automatic Allocation",
        description:
          "Confirms an admin has reviewed the company's allocation strategy, so the allocation job may start moving its GOs into its portfolios. Until then the GOs stay unallocated and Slack gets a reminder.",
      },
    ],
  },
  {
    id: "producer_onboarding",
    label: "Producer onboarding",
    views: PRODUCER_VIEWS,
    flags: [
      {
        key: "international_self_signup",
        label: "International Self-Signup",
        description:
          "Set automatically on a producer outside Estonia who signed up on their own: nothing is signed yet, so the dashboard asks them to book an onboarding demo with sales. It does not make the company trusted.",
      },
    ],
  },
  {
    id: "producer_strategy",
    label: "Production strategy & devices",
    views: PRODUCER_VIEWS,
    flags: [
      {
        key: "forward_sales_enabled",
        label: "Forward Sales",
        description:
          "Lets the producer pre-sell future production at an agreed price through a forward sales mandate. Turning it on creates the Forward portfolio; turning it off is refused while forward volume is owed or escrow is held.",
      },
      {
        key: "hold_allocation",
        label: "Hold Allocation",
        description:
          "Lets the producer set aside part of their GOs to manage themselves instead of having Soldera sell them, and sell those manually from the inventory.",
      },
      {
        key: "ppa_allocation",
        label: "PPA Allocation",
        description:
          "For producers with a Power Purchase Agreement: part of their GOs is reserved for the PPA buyer and delivered automatically on the agreed schedule.",
      },
      {
        key: "manual_production_estimates",
        label: "Manual Production Estimates",
        description:
          "For devices whose formula-based estimate (from capacity, technology and country) is wrong or missing, the company enters 12 monthly MWh figures instead. The estimates feed production projections and forward sales feasibility checks.",
      },
      {
        key: "income_estimate_enabled",
        label: "Income Estimate",
        description:
          "A sales showcase page that shows static demo figures, not the company's real income. Enable it only for demos.",
      },
    ],
  },
  {
    id: "trader",
    label: "Trader",
    views: ["trader"],
    flags: [
      {
        key: "buy_EACs_enabled",
        label: "Buy EACs Enabled",
        description:
          "Lets a trader also act as a buyer and order EACs through Soldera; corporations can always buy.",
      },
    ],
  },
  {
    id: "corporate",
    label: "Corporate",
    views: ["corporate"],
    flags: [
      {
        key: "corporate_virtual_accounts_enabled",
        label: "Corporate Hosted Accounts Enabled",
        description:
          "Soldera holds the corporation's EACs in its own registry accounts, so the corporation can receive, send on and cancel EACs without a registry account of its own.",
      },
    ],
  },
  {
    id: "ecolabels",
    label: "Ecolabels",
    views: ALL_COMPANY_VIEWS,
    flags: [
      {
        key: "ecolabels_enabled",
        label: "Ecolabels Enabled",
        description:
          "Opens the EKOenergy device catalog to the company's users for viewing. Selling EKOenergy cancellation statements needs EKOenergy Authorized Seller instead.",
      },
      {
        key: "ekoenergy_authorized_seller",
        label: "EKOenergy Authorized Seller",
        description:
          "EKOenergy has authorized the company to sell EKOenergy cancellation statements in the portal; EKOenergy admins set it when they create a seller. Such a company is trusted and is not billed a Soldera subscription.",
      },
    ],
  },
  {
    id: "billing",
    label: "Billing & payouts",
    views: ALL_COMPANY_VIEWS,
    flags: [
      {
        key: "ledger_reads",
        label: "Ledger Reads",
        description:
          "Rollout switch for the DEV-579 obligation ledger: the company's invoices are computed from the ledger instead of the legacy invoice tables. Needed for non-EUR payouts, and cannot be turned off while a non-EUR invoice is live.",
      },
      {
        key: "month_aligned_payout_invoice",
        label: "Month-aligned payout invoice (EU)",
        description:
          "Keeps a payout invoice in the month the sales happened, which EU producers' accounting expects: a payout requested by the 10th covers the previous month on an invoice dated that month's last day. On by default for EU companies.",
      },
      {
        key: "skip_first_year_referral",
        label: "Skip First Year Referral",
        description:
          "The partner who referred this company earns no referral commission on its sales during the company's first membership year.",
      },
    ],
  },
  {
    id: "producer_billing",
    label: "Producer invoicing",
    views: PRODUCER_VIEWS,
    flags: [
      {
        key: "invoice_device_breakdown",
        label: "Invoice Device Breakdown",
        description:
          "For producers who need to see what each device earned: every payout invoice line is split per device.",
      },
    ],
  },
] as const satisfies readonly {
  id: string;
  label: string;
  views: readonly CompanyView[];
  flags: readonly { key: string; label: string; description: string }[];
}[];

export type CompanyFeatureFlagKey =
  (typeof FEATURE_FLAG_GROUPS)[number]["flags"][number]["key"];

export const FEATURE_FLAGS = Object.fromEntries(
  FEATURE_FLAG_GROUPS.flatMap((group) =>
    group.flags.map((flag) => [flag.key, flag.label])
  )
) as Record<CompanyFeatureFlagKey, string>;

// Account Feature Flags
// Feature flags are defined in the AccountFeatureFlags model in app/models/account_models.py
// When adding a new feature flag:
// 1. Add the field to AccountFeatureFlags model in app/models/account_models.py
// 2. Add its human-readable label and admin tooltip description to this mapping
export const ACCOUNT_FEATURE_FLAGS = {
  hknr_bulk_issuance_report_enabled: {
    label: "HKNR Bulk Issuance Report",
    description:
      "For a service provider managing many producers in the German HKNR registry: issue GOs for all of them in one go and export the TSO declaration Excel, from the consolidated view.",
  },
  ekoenergy_admin_enabled: {
    label: "EKOenergy Admin Access",
    description:
      "For EKOenergy's own staff, who run the ecolabel programme in the portal: they review device submissions (and get their emails), see every device and label, and create authorized seller companies.",
  },
  detailed_reservoir_levels_enabled: {
    label: "Detailed Reservoir Levels",
    description:
      "Unlocks reservoir levels for every Norwegian zone in Market Insights, which other users see locked with a 'contact us' upsell. Admins always see all zones.",
  },
  compliance_groups_demo_enabled: {
    label: "Compliance Groups Demo",
    description:
      "A clickable prototype of compliance groups for corporate EAC coverage, running on fixture data in the browser. For demos and design feedback only.",
  },
  realtime_soldera_inventory: {
    label: "Realtime Soldera Inventory",
    description:
      "Lets a sales partner sell from Soldera's live spot and forward supply: they see it with producer identities hidden and send offers for any counterparty, which Soldera staff confirm.",
  },
} as const satisfies Record<string, { label: string; description: string }>;

// Month names for charts and date displays
export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
